import { Routes, Route, useLocation } from "react-router-dom";
import "./i18n";
import { I18nextProvider, useTranslation } from "react-i18next";
import axios from "axios";
import { useEffect, useState } from "react";
import "./exo/stylesheet.css";
import RoadMap from "./page/roadMap/roadMap";
import Login from "./page/login/login";
import SignUp from "./page/signUp/signUp";
import Main from "./page/mainPage/main";
import Catalog from "./page/catalog/catalog";
import Lesson from "./page/lesson/lesson";
import NotFound from "./components/404/notFound";
import { checkAuthStatus } from "./lib/checkAuthStatus";
import Survey from "./page/survey/survey";
import Pricing from "./page/pricing/pricing";
import TermsOfUse from "./page/infoPages/termsOfUse";
import TermsOfUsePage from "./page/infoPages/termsOfUse";
import PrivacyPage from "./page/infoPages/privacyPage";
import RefPage from "./page/infoPages/refPage";
import PaymentStatusPage from "./page/infoPages/paymentStatusPage";
import { initGA4, trackPageView } from "./lib/analytics";
import { useUTM } from "./hooks/useUTM";

export default function App() {
  const [authUser, setAuthUser] = useState(null);
  const [checkingDone, setCheckingDone] = useState(false);
  const { i18n } = useTranslation();
  const location = useLocation();

  // Initialize analytics and UTM tracking
  useUTM();
  
  useEffect(() => {
    // Initialize GA4 when component mounts
    initGA4();
  }, []);

  useEffect(() => {
    const init = async () => {
      const user = await checkAuthStatus();
      setAuthUser(user);
      setCheckingDone(true);
      
      // Set user_id for GA4 if authenticated
      if (user?.isAuthenticated && user?.user?.id) {
        window.gtag?.('set', { user_id: user.user.id.toString() });
      }

      // Only fetch user language if authenticated
      if (user?.isAuthenticated) {
        try {
          const res = await axios.get("/api/v1/user-profile/current", {
            withCredentials: true,
          });
          const lang = res.data.language || "en";
          i18n.changeLanguage(lang);
        } catch (error) {
          console.debug("Error fetching user language:", error.message);
        }
      }
    };

    init();
  }, [location.pathname, i18n]);

  // Function to refresh auth user data (exposed globally for child components)
  const refreshAuthUser = async () => {
    try {
      console.log('🔄 Refreshing authUser data...');
      const user = await checkAuthStatus();
      setAuthUser(user);
      console.log('✅ AuthUser refreshed:', user);
      return user;
    } catch (error) {
      console.error('❌ Error refreshing authUser:', error);
      return null;
    }
  };

  // Make refreshAuthUser available globally
  useEffect(() => {
    window.refreshAuthUser = refreshAuthUser;
    return () => {
      delete window.refreshAuthUser;
    };
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (checkingDone) {
      trackPageView(location.pathname, document.title);
    }
  }, [location.pathname, checkingDone]);

  if (!checkingDone) return null;

  return (
    <I18nextProvider i18n={i18n}>
      <Routes>
        <Route path="/signin" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/survey" element={<Survey authUser={authUser} />}  />
        <Route path="/roadMap" element={<RoadMap authUser={authUser} />} />
        <Route path="/" element={<Main authUser={authUser} />} />
        <Route path="/catalog" element={<Catalog authUser={authUser} />} />
        <Route path="/lesson/:id" element={<Lesson authUser={authUser} />} />
        <Route path="/pricing" element={<Pricing authUser={authUser} />} />
        <Route path="*" element={<NotFound authUser={authUser} />} />
        <Route path="/termsOfUse" element={<TermsOfUsePage authUser={authUser} />} />
        <Route path="/privacy" element={<PrivacyPage authUser={authUser} />} />
        <Route path="/cancelAndRefund" element={<RefPage authUser={authUser} />} />
        <Route path="/paymentStatus" element={<PaymentStatusPage authUser={authUser} />} />
      </Routes>
    </I18nextProvider>
  );
}
